import { posthog, environment } from '@/config/posthog.config';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Broad payload type accepted by the base `track`/`identify`/`group` calls.
 *
 * This deliberately stays a wide index signature: `trackEvent` is the universal
 * PostHog entry point used by non-dashboard call sites across the app, and
 * tightening it here would ripple into every caller. For type-checked dashboard
 * payloads, use {@link DashboardEventProperties} instead (it is assignable to
 * this type, so dashboard callers can opt in without changing the base API).
 */
export interface PostHogEventProperties {
  // `Record<string, number>` admits the one nested payload we send: the dashboard
  // load summary's `query_timings` map (`{ "<query_id>": data_ms }`). PostHog stores
  // it as property JSON. Kept deliberately narrow — a free-form object here would
  // reopen the PII/bloat door the backend whitelist exists to close.
  [key: string]: string | number | boolean | undefined | null | Record<string, number>;
}

/**
 * Opt-in, type-checked payload shape for dashboard adoption/usage events.
 *
 * These are the bounded, low-cardinality dimensions that the dashboard tracking
 * choke-point (`DashboardTrackingContext`) auto-merges and that the backend
 * `adoption_metrics` dual-write whitelists. Dashboard callers should annotate
 * their payloads with this type to catch typos / wrong field types at compile
 * time. It extends {@link PostHogEventProperties}, so it remains accepted by the
 * base `trackEvent` signature with no change to non-dashboard call sites.
 *
 * NOTE: free-form, high-cardinality, or PII-bearing values (raw filter values,
 * search strings, drilldown labels) must NOT be added here — see the backend
 * whitelist in `services/dashboardMetrics.ts`.
 */
export interface DashboardEventProperties extends PostHogEventProperties {
  dashboard_id?: string;
  dashboard_title?: string;
  active_tab_id?: string;
  tab_id?: string;
  widget_type?: string;
  chart_type?: string;
  source?: string;
  filter_id?: string;
  drilldown_id?: string;
  duration_ms?: number;
  /** Schema v2 stable widget/section ids (`Visualization.id`/`KPI.id`/`Section.id`);
   * a synthesized legacy key on v1 specs that carry none. See `getWidgetId`/`getSectionId`. */
  widget_id?: string;
  section_id?: string;
  query_id?: string;

  // ── Performance telemetry (plan §4) ──
  /** Which user-perceived wait this load belongs to (`DashboardLoadKind`). */
  load_kind?: string;
  /** Batch request start → this widget's result arrival. */
  data_ms?: number;
  /** This widget's data-ready → post-paint. */
  render_ms?: number;
  /** Batch start → FIRST result in the batch (time to first render-able widget). */
  ttfr_ms?: number;
  slowest_query_id?: string;
  slowest_widget_id?: string;
  slowest_ms?: number;
  query_count?: number;
  error_count?: number;
  no_data_count?: number;
  cache_hits?: number;
  row_count?: number;
  /** The backend served this result from its query cache. */
  cached?: boolean;
  /** `{ "<query_id>": data_ms }` for the whole batch. */
  query_timings?: Record<string, number>;
  /** This tab's batch ended in a transport failure — its `dashboard_tab_viewed` times a wait that never paid off. */
  failed?: boolean;
}

export const usePostHogTracking = () => {
  const trackEvent = useCallback((eventName: string, properties?: PostHogEventProperties) => {
    if (!posthog) return;
    try {
      posthog.capture(eventName, { ...properties });
    } catch (error) {
      console.error(`PostHog: Error tracking event ${eventName}`, error);
    }
  }, []);

  const identifyUser = useCallback((distinctId: string, properties?: PostHogEventProperties) => {
    if (!posthog) return;
    try {
      posthog.identify(distinctId, { ...properties });
      posthog.register({ ...properties, environment });

    } catch (error) {
      console.error('PostHog: Error identifying user', error);
    }
  }, []);

  const resetUser = useCallback(() => {
    if (!posthog) return;
    try {
      posthog.reset();
    } catch (error) {
      console.error('PostHog: Error resetting user', error);
    }
  }, []);

  /**
   * Associates the current user with a group (PostHog group analytics) so events
   * roll up at that level — e.g. `group('company', companyId)` enables
   * company-level usage/adoption analysis. `posthog.reset()` (resetUser) clears
   * groups too.
   */
  const group = useCallback((groupType: string, groupKey: string, properties?: PostHogEventProperties) => {
    if (!posthog || !groupKey) return;
    try {
      posthog.group(groupType, groupKey, properties ? { ...properties } : undefined);
    } catch (error) {
      console.error(`PostHog: Error setting group ${groupType}`, error);
    }
  }, []);

  return {
    trackEvent,
    identifyUser,
    resetUser,
    group,
  };
};

export const usePostHogPageView = () => {
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog) return;
    
    const currentPath = location.pathname;
    
    if (previousPathRef.current !== currentPath) {
      const urlWithoutParams = `${window.location.origin}${location.pathname}`;
      
      posthog.capture('$pageview', {
        $current_url: urlWithoutParams,
        $pathname: location.pathname,
        environment,
      });
      previousPathRef.current = currentPath;
    }
  }, [location.pathname]);
};
